import React, { Suspense, useEffect, useMemo } from "react";
import { Header, Message, Segment } from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import WorkList from "../components/worklist/WorkList";
import { TLoadable, isLoaded, isUnloaded, isLoading, isLoadErrored } from "../../../sg-core";
import IObservation from "../interfaces/IObservation";
import { LoaderSegment } from "../components/LoaderSegment";
import { useObservationListPage, useObservationPagination } from "../store/selectors/observationListSelectors";
import { WorkListFilters } from "../components/worklist/WorkListFilters";
import { WorkListPagination } from "../components/worklist/WorkListPagination";
import { useLocation } from "react-router-dom";
import AppMessage from "../components/message/AppMessage";
import { useDispatch } from "react-redux";
import { createClearObservationDeleagtionAction, createClearObservationProcessForm, createClearCreateObservationAction } from "../store/actions/observationActions";
import { TFunction } from "i18next";
import { PageTemplate } from "../components/PageTemplate/PageTemplate";

const getTextProps = (t: TFunction) => ({
    headerText: t("Tehdyt havainnot"),
    noResultText: t("Ei tuloksia"),
    failedText: t("Haussa tapahtui virhe")

});

interface IWorklistPageProps extends ReturnType<typeof getTextProps> {
    workList: TLoadable<IObservation[]>;

}

const WorklistPage: React.FC<IWorklistPageProps> = ({
    headerText,
    workList,
    noResultText,
    failedText
}) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { state } = useLocation<{ messageId?: string }>();

    const listIsEmpty = isLoaded(workList) && workList.data.length === 0;

    useEffect(() => {
        dispatch(createClearObservationDeleagtionAction());
        dispatch(createClearObservationProcessForm());
        dispatch(createClearCreateObservationAction())
    }, []);
    const { pagination: { totalItems } } = useObservationPagination();

    return (
        <PageTemplate>
            {state?.messageId ? <AppMessage messageId={state.messageId} /> : null}

            <Header as="h1">{headerText} {totalItems ? `(${totalItems})` : null} </Header>

            <WorkListFilters />

            {(isUnloaded(workList) || isLoading(workList)) && <LoaderSegment />}


            <Suspense fallback={<LoaderSegment />}>
                {isLoaded(workList) && <WorkList workList={workList.data} />}
            </Suspense>

            {isLoadErrored(workList) && (
                <Message content={failedText} negative />
            )}

            {listIsEmpty && (
                <Segment textAlign="center">{noResultText}</Segment>
            )}

            <WorkListPagination />

        </PageTemplate>
    );
};

const MemoizedWorklistPage = React.memo(WorklistPage);

const WorklistPageContainer: React.FC = () => {
    const { t } = useTranslation();
    const textProps = useMemo(() => getTextProps(t), [t]);
    const workList = useObservationListPage();

    return (
        <MemoizedWorklistPage   {...textProps} workList={workList} />
    );
};

export default WorklistPageContainer;
